/**
 * Les projets montres dans le carrousel de /projets et dans la selection de
 * l'accueil.
 *
 * Pourquoi une liste en dur plutot que l'API : au 12 aout 2026, /api/projects/
 * ne connait que trois entrees (publiar, housing-ai, dkbois). Find It Now,
 * Gridar et Send Me Now n'y sont pas, alors qu'ils sont en ligne.
 * A migrer vers le backend Django des que les trois manquants y sont crees ;
 * ce fichier redeviendra alors un simple repli.
 *
 * Regle : rien ici qui ne soit verifiable sur le site en question.
 */

export interface Projet {
  slug: string;
  title: string;
  tagline: string;
  url: string;
  stack: string[];
  /** Capture au format telephone, pour l'anneau 3D */
  mobile: string;
  /** Mockup fenetre de navigateur, fond transparent. Base du nom, sans taille. */
  mockup?: string;
  /** Mandat facture, par opposition a un produit personnel */
  client: boolean;
  /** Mis en avant dans la selection de trois de l'accueil */
  selection: boolean;
  /**
   * Rendu laptop de face, fond transparent : visuel principal de la fiche
   * /projets/<slug> et son og:image. Base du nom dans /public/projets, sans
   * taille : il existe en 900 et 1400.
   */
  laptop?: string;
  /** Galerie de la fiche, chemins complets. Les rendus laptop sont en 1200. */
  galerie?: { src: string; alt: string }[];
}

export const PROJETS: Projet[] = [
  {
    slug: "dkbois",
    title: "FDKbois",
    tagline: "Menuiserie et ébénisterie à Yaoundé. Catalogue, demande de devis en ligne, espace admin.",
    url: "https://fdkbois.com",
    stack: ["Django", "React", "PostgreSQL"],
    mobile: "/projets/fdkbois-mobile.webp",
    mockup: "fdkbois-mockup",
    laptop: "fdkbois-laptop-face",
    galerie: [
      { src: "/projets/fdkbois-laptop-left-1200.webp", alt: "FDKbois, page d'accueil, ordinateur portable vu de trois-quarts gauche" },
      { src: "/projets/fdkbois-laptop-2-1200.webp", alt: "FDKbois, autre écran de l'application, sur ordinateur portable" },
      { src: "/projets/fdkbois-laptop-3-1200.webp", alt: "FDKbois, autre écran de l'application, sur ordinateur portable" },
      { src: "/projets/fdkbois-mobile.webp", alt: "FDKbois sur téléphone" },
    ],
    client: true,
    selection: false,
  },
  {
    slug: "sendmenow",
    title: "Send Me Now",
    tagline: "Messages anonymes éphémères par code QR, avec boutique en ligne.",
    url: "https://sendmenow.ca",
    stack: ["Django", "React", "Stripe"],
    mobile: "/projets/sendmenow-mobile.webp",
    mockup: "sendmenow-mockup",
    laptop: "sendmenow-laptop-face",
    galerie: [
      { src: "/projets/sendmenow-laptop-left-1200.webp", alt: "Send Me Now, page d'accueil, ordinateur portable vu de trois-quarts gauche" },
      { src: "/projets/sendmenow-laptop-right-1200.webp", alt: "Send Me Now, page d'accueil, ordinateur portable vu de trois-quarts droit" },
      { src: "/projets/sendmenow-mobile.webp", alt: "Send Me Now sur téléphone" },
    ],
    client: true,
    selection: false,
  },
  {
    slug: "gridar",
    title: "Gridar",
    tagline: "Audit SEO et mesure de la visibilité dans les réponses des IA.",
    url: "https://gridar.app",
    stack: ["Django", "React", "TypeScript"],
    mobile: "/projets/gridar-mobile.webp",
    mockup: "gridar-mockup",
    laptop: "gridar-laptop-face",
    galerie: [
      { src: "/projets/gridar-laptop-left-1200.webp", alt: "Gridar, page d'accueil, ordinateur portable vu de trois-quarts gauche" },
      { src: "/projets/gridar-laptop-2-1200.webp", alt: "Gridar, autre écran de l'application, sur ordinateur portable" },
      { src: "/projets/gridar-laptop-3-1200.webp", alt: "Gridar, autre écran de l'application, sur ordinateur portable" },
    ],
    client: false,
    selection: true,
  },
  {
    slug: "finditnow",
    title: "Find It Now",
    tagline: "Objets perdus retrouvés par code QR, sans donner son numéro ni son adresse.",
    url: "https://finditnow-frontend.vercel.app",
    stack: ["Django", "Channels", "Next.js", "Stripe"],
    mobile: "/projets/finditnow-mobile.webp",
    mockup: "finditnow-mockup",
    laptop: "finditnow-laptop-face",
    galerie: [
      { src: "/projets/finditnow-laptop-left-1200.webp", alt: "Find It Now, page d'accueil, ordinateur portable vu de trois-quarts gauche" },
      { src: "/projets/finditnow-laptop-2-1200.webp", alt: "Find It Now, autre écran de l'application, sur ordinateur portable" },
      { src: "/projets/finditnow-mobile.webp", alt: "Find It Now sur téléphone" },
    ],
    client: true,
    selection: true,
  },
  {
    slug: "housing-ai",
    title: "LocaSur",
    tagline: "Marketplace locataires et propriétaires au Québec, avec références vérifiées.",
    url: "https://housing-ai.ca",
    stack: ["Django", "Celery", "Redis", "React"],
    mobile: "/projets/locasur-mobile.webp",
    mockup: "locasur-mockup",
    laptop: "locasur-laptop-face",
    galerie: [
      { src: "/projets/locasur-laptop-left-1200.webp", alt: "LocaSur, page d'accueil, ordinateur portable vu de trois-quarts gauche" },
      { src: "/projets/locasur-laptop-2-1200.webp", alt: "LocaSur, autre écran de l'application, sur ordinateur portable" },
      { src: "/projets/locasur-laptop-3-1200.webp", alt: "LocaSur, autre écran de l'application, sur ordinateur portable" },
    ],
    client: false,
    selection: true,
  },
  {
    slug: "publiar",
    title: "Publiar",
    tagline: "Lead magnets LinkedIn construits avec un hook, une preuve et un visuel.",
    url: "https://publiar.app",
    stack: ["Django", "React", "TypeScript"],
    mobile: "/projets/publiar-mobile.webp",
    mockup: "publiar-mockup",
    laptop: "publiar-laptop-face",
    galerie: [
      { src: "/projets/publiar-laptop-left-1200.webp", alt: "Publiar, page d'accueil, ordinateur portable vu de trois-quarts gauche" },
      { src: "/projets/publiar-laptop-2-1200.webp", alt: "Publiar, autre écran de l'application, sur ordinateur portable" },
      { src: "/projets/publiar-laptop-3-1200.webp", alt: "Publiar, autre écran de l'application, sur ordinateur portable" },
    ],
    client: false,
    selection: false,
  },
];

export const SELECTION = PROJETS.filter((p) => p.selection);
